import { Form, Field, FieldProps } from "formik"
import { ChangeEvent, FC, useEffect } from "react"
import { InputField } from "./InputField"
import { InputRangeWithField } from "./InputRangeWithField"
import { isEmpty, isNaN } from "lodash"
import { logEvent } from "../../utils/logEvent"
import { InputRadioGroup } from "./InputRadioGroup"
import { FormValues } from "../../types"
import useFormValuesStore from "../../store/useFormValuesStore"

export type InputFormProps = {
  /**
   * Existing response data used to display monthly payment
   */
  responseData?: any
  /**
   * Form errors
   */
  errors: any
  /**
   * Field of values which indicates if a field was interacted with
   */
  touched: any
  /**
   * Form handle change function
   */
  handleChange: (
    event: ChangeEvent<HTMLInputElement> | { target: { name: string; value: { id: number; value: string } } }
  ) => void
  /**
   * Field handle set field value
   */
  setFieldValue: any
  /**
   * Sets all fields to readonly
   */
  readOnly?: boolean
  /**
   * Function used to switch between tabs
   */
  setTabIndex?: (value: number) => void
  /**
   * Function used to submit form
   */
  onSubmit?: (value: FormValues) => void
}

const validateNumber = (n: any) => {
  let error

  if (isNaN(parseFloat(n)) && !isNaN(n - 0)) {
    error = "Please enter a number."
  }

  return error
}

const validateCoupon = (n: any) => {
  let error
  const parsedFloat = parseFloat(n)
  if (isNaN(parsedFloat) && !isNaN(n - 0)) {
    error = "Please enter a number."
  }
  if (parsedFloat > 100) {
    error = "Number should be below 100%."
  } else if (parsedFloat < 0) {
    error = "Number should be above 0."
  }

  return error
}

const validateSpread = (n: any, values: any) => {
  let error

  const cleanValue = String(n).replaceAll("%", "")
  const parsedFloat = parseFloat(n)

  if (isNaN(parseFloat(cleanValue)) && !isNaN(Number(cleanValue) - 0)) {
    error = "Please enter a number."
  }

  if (Number(cleanValue) > Number(values.Coupon)) {
    error = "Spread value has to be smaller than Coupon value."
  }

  if (parsedFloat > 1) {
    error = "Number should be below 1."
  } else if (parsedFloat < 0.0) {
    error = "Number should be above 0."
  }

  return error
}

export const InputForm: FC<InputFormProps> = ({
  responseData = null,
  errors,
  touched,
  handleChange,
  setFieldValue,
  readOnly = false,
  setTabIndex = () => {},
  onSubmit,
}) => {
  const { values } = useFormValuesStore((state) => state)

  const handleFormSubmit = () => {
    logEvent("DemoAppCalculate")
    setTabIndex(1)
    if (onSubmit && values) {
      onSubmit(values)
    }
  }

  useEffect(() => {
    if (values["Coupon Frequency"] === "") {
      setFieldValue("Coupon Frequency", "Monthly")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Form className="flex flex-col items-center justify-center pb-6 space-y-3">
      {!readOnly && (
        <div className="mx-auto w-full max-w-sm mb-8 text-center">
          <p className="text-sm mb-2">Bond market value:</p>
          <span className="text-5xl">
            $
            {Number.parseFloat(
              responseData?.outputs && !isNaN(responseData.outputs.Bond_MV) ? responseData.outputs.Bond_MV : 0
            ).toFixed(2)}
          </span>
        </div>
      )}

      <Field name="Valuation Date">
        {({ field }: FieldProps) => (
          <div className="mx-auto w-full max-w-sm">
            <InputField
              id="Valuation Date"
              label="Valuation Date"
              type="date"
              value={field.value}
              handleChange={handleChange}
              error={errors["Valuation Date"]}
              touched={touched["Valuation Date"]}
              readOnly={readOnly}
              max={values["Maturity Date"]}
            />
          </div>
        )}
      </Field>

      <Field name="Maturity Date">
        {({ field }: FieldProps) => (
          <div className="mx-auto w-full max-w-sm">
            <InputField
              id="Maturity Date"
              label="Maturity Date"
              type="date"
              value={field.value}
              handleChange={handleChange}
              error={errors["Maturity Date"]}
              touched={touched["Maturity Date"]}
              readOnly={readOnly}
              min={values["Valuation Date"]}
            />
          </div>
        )}
      </Field>

      <Field name="Coupon" validate={validateCoupon}>
        {({ field }: FieldProps) => (
          <div className="mx-auto w-full max-w-sm">
            <InputField
              suffix="%"
              id="Coupon"
              label="Coupon"
              type="number"
              value={field.value}
              handleChange={handleChange}
              error={errors.Coupon}
              touched={touched.Coupon}
              readOnly={readOnly}
            />
          </div>
        )}
      </Field>

      <Field name="Coupon Frequency" validate={validateNumber}>
        {({ field }: FieldProps) => (
          <div className="mx-auto w-full max-w-sm">
            <InputRadioGroup
              id="Coupon Frequency"
              label="Coupon Frequency"
              options={["Monthly", "Quarterly", "Semi-Annual", "Annual"]}
              value={field.value}
              handleChange={(val) => {
                setFieldValue("Coupon Frequency", val)
              }}
              readOnly={readOnly}
            />
          </div>
        )}
      </Field>

      <Field name="Redemption Amount" validate={validateNumber}>
        {({ field }: FieldProps) => (
          <div className="mx-auto w-full max-w-sm">
            <InputRangeWithField
              id="Redemption Amount"
              label="Redemption Amount"
              value={field.value}
              handleChange={handleChange}
              error={errors["Redemption Amount"]}
              touched={touched["Redemption Amount"]}
              min={20}
              max={100}
              step={5}
              readOnly={readOnly}
            />
          </div>
        )}
      </Field>

      <Field name="Spread" validate={(n: string) => validateSpread(n, values)}>
        {({ field }: FieldProps) => (
          <div className="mx-auto w-full max-w-sm">
            <InputField
              suffix="%"
              id="Spread"
              label="Spread"
              type="number"
              value={field.value}
              handleChange={handleChange}
              error={errors.Spread}
              touched={touched.Spread}
              readOnly={readOnly}
            />
          </div>
        )}
      </Field>

      {!readOnly && (
        <button
          className="bg-brand-light rounded text-white py-2 px-8 mt-3 text-sm"
          type="button"
          onClick={handleFormSubmit}
          disabled={!isEmpty(errors)}
        >
          CALCULATE
        </button>
      )}
    </Form>
  )
}

